import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Phone,
  Email,
  LocationOn,
  Visibility,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

import { useAuth } from '../contexts/AuthContext.tsx';
import { customersApi } from '../services/api.ts';
import { Customer, CustomerCreate } from '../types';

// Add customer dialog component
interface AddCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (customer: CustomerCreate) => void;
}

const AddCustomerDialog: React.FC<AddCustomerDialogProps> = ({ open, onClose, onAdd }) => {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<CustomerCreate>();

  const onSubmit = (data: CustomerCreate) => {
    onAdd(data);
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Customer</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="first_name"
                control={control}
                rules={{ required: 'First name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="First Name"
                    fullWidth
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="last_name"
                control={control}
                rules={{ required: 'Last name is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Last Name"
                    fullWidth
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone"
                    fullWidth
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="address_line_1"
                control={control}
                rules={{ required: 'Address is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Address Line 1"
                    fullWidth
                    error={!!errors.address_line_1}
                    helperText={errors.address_line_1?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="address_line_2"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Address Line 2"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="city"
                control={control}
                rules={{ required: 'City is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="City"
                    fullWidth
                    error={!!errors.city}
                    helperText={errors.city?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="county"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="County"
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="postcode"
                control={control}
                rules={{ required: 'Postcode is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Postcode"
                    fullWidth
                    error={!!errors.postcode}
                    helperText={errors.postcode?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="property_type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Property Type</InputLabel>
                    <Select {...field} label="Property Type">
                      <MenuItem value="detached">Detached</MenuItem>
                      <MenuItem value="semi-detached">Semi-Detached</MenuItem>
                      <MenuItem value="terraced">Terraced</MenuItem>
                      <MenuItem value="flat">Flat</MenuItem>
                      <MenuItem value="bungalow">Bungalow</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="lead_source"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel>Lead Source</InputLabel>
                    <Select {...field} label="Lead Source">
                      <MenuItem value="website">Website</MenuItem>
                      <MenuItem value="referral">Referral</MenuItem>
                      <MenuItem value="advertising">Advertising</MenuItem>
                      <MenuItem value="social_media">Social Media</MenuItem>
                      <MenuItem value="cold_call">Cold Call</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    multiline
                    rows={3}
                    fullWidth
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Add Customer
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const { isManager, isSurveyor } = useAuth();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Fetch customers with search and pagination
  const { data: customersData, isLoading, error } = useQuery(
    ['customers', { search: searchTerm, skip: page * rowsPerPage, limit: rowsPerPage }],
    () => customersApi.getCustomers({
      search: searchTerm || undefined,
      skip: page * rowsPerPage,
      limit: rowsPerPage,
    }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  // Add customer mutation
  const addCustomerMutation = useMutation(
    (customer: CustomerCreate) => customersApi.createCustomer(customer),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('customers');
      },
    }
  );

  // Delete customer mutation
  const deleteCustomerMutation = useMutation(
    (customerId: number) => customersApi.deleteCustomer(customerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('customers');
      },
    }
  );

  const handleAddCustomer = (customer: CustomerCreate) => {
    addCustomerMutation.mutate(customer);
  };

  const handleDeleteCustomer = (customerId: number) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      deleteCustomerMutation.mutate(customerId);
    }
  };

  const handleViewCustomer = (customerId: number) => {
    navigate(`/customers/${customerId}`);
  };

  const getLeadSourceColor = (source: string) => {
    switch (source) {
      case 'website':
        return 'primary';
      case 'referral':
        return 'success';
      case 'advertising':
        return 'warning';
      case 'social_media':
        return 'info';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load customers. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Customers
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {customersData?.total || 0} total customers
          </Typography>
        </Box>
        {(isManager || isSurveyor) && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Customer
          </Button>
        )}
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search customers by name, email, postcode, or city..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Customers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Property</TableCell>
              <TableCell>Lead Source</TableCell>
              <TableCell>Added</TableCell>
              {(isManager || isSurveyor) && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 7 }).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : customersData?.customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {searchTerm ? 'No customers found matching your search.' : 'No customers found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              customersData?.customers.map((customer) => (
                <TableRow key={customer.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {customer.full_name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{customer.email}</Typography>
                      </Box>
                      {customer.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{customer.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2">{customer.city}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {customer.postcode}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      {customer.property_type && (
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {customer.property_type.replace('_', ' ')}
                        </Typography>
                      )}
                      {customer.floor_area && (
                        <Typography variant="body2" color="text.secondary">
                          {customer.floor_area}m²
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {customer.lead_source && (
                      <Chip
                        label={customer.lead_source.replace('_', ' ')}
                        size="small"
                        color={getLeadSourceColor(customer.lead_source) as any}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(customer.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  {(isManager || isSurveyor) && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleViewCustomer(customer.id)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                      {isManager && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteCustomer(customer.id)}
                          title="Delete Customer"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {customersData && (
          <TablePagination
            component="div"
            count={customersData.total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </TableContainer>

      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddCustomer}
      />
    </Box>
  );
};

export default CustomersPage;